import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { withRouter, RouteComponentProps } from "react-router";
import { selectRoute, Route } from "src/config/routes";
import { Link } from "react-router-dom";
import { FallbackWorkflowUserRole } from "src/stores/KrediFlowConfigurationStore";
import { FallbackWorkflowUserField } from "src/screens/krediflowConfigurationSettings/fallbackWorkflowUserField/FallbackWorkflowUserField";
import styles from "./KrediFlowConfigurationSettings.module.css";

const FALLBACK_FIELDS: FallbackWorkflowUserRole[] = ["visa1", "visa2", "visa3", "accountant"];

const KrediflowConfigurationSettingsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, uiSidebarStore, krediFlowConfigurationStore } = useContext(RootStoreContext);

    useEffect(() => {
        krediFlowConfigurationStore.init();
    }, [krediFlowConfigurationStore]);

    useEffect(() => {
        navStore.setTitle(t("screens.kredi_flow_settings.title"));
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        if (uiSidebarStore.isSidebarActive) {
            navStore.setBackbutton(null);
        } else {
            navStore.setBackbutton(selectRoute(Route.profile), t("screens.account.title").toString());
        }
    }, [navStore, t, uiSidebarStore.isSidebarActive]);

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col xl={9}>
                        <UI.Title>{t("screens.kredi_flow_settings.title")}</UI.Title>
                    </UI.Col>
                </UI.Row>
                {navStore.hasFeatureKrediFlowPro && (
                    <UI.Row>
                        <UI.Col md={12}>
                            <UI.Card
                                title={t("screens.kredi_flow_settings.visaworkflow.title").toString()}
                                className={styles.VisaworkflowSettingsFormContainer}
                            >
                                <div className="d-flex align-items-center" style={{ height: 25 }}>
                                    <UI.Switch
                                        checked={krediFlowConfigurationStore.manualVisaworkflowDefinition}
                                        onToggle={krediFlowConfigurationStore.toggleManualVisaworkflowDefinition}
                                    />
                                    <div className="ml-auto">
                                        {krediFlowConfigurationStore.manualVisaworkflowDefinition ? (
                                            <Link to={Route.krediflowConfigurationSettingsRealestateVisas}>
                                                {t("screens.kredi_flow_settings.visaworkflow.customize").toString()}
                                            </Link>
                                        ) : (
                                            ""
                                        )}
                                    </div>
                                </div>
                            </UI.Card>
                        </UI.Col>
                    </UI.Row>
                )}
                <UI.Row>
                    <UI.Col md={12}>
                        <UI.Card
                            title={t("screens.kredi_flow_settings.number_of_visa.title").toString()}
                            className={styles.VisaworkflowSettingsFormContainer}
                        >
                            <UI.Row className="mb-3">
                                <UI.Col>
                                    <span>{t("screens.kredi_flow_settings.number_of_visa.explanation_text")}</span>
                                </UI.Col>
                            </UI.Row>
                            <UI.Row className="align-items-center">
                                <UI.Col md={1} lg={1}>
                                    <UI.Dropdown
                                        id="number-of-visa"
                                        label={krediFlowConfigurationStore.numberOfVisa.toString()}
                                        items={Array.from(
                                            { length: krediFlowConfigurationStore.maxNumberOfVisa },
                                            (_, index) => {
                                                const visaCount = (index + 1).toString();
                                                return { label: visaCount, value: visaCount };
                                            }
                                        )}
                                        onSelect={(value: string) =>
                                            krediFlowConfigurationStore.setNumberOfVisa(Number(value))
                                        }
                                    />
                                </UI.Col>
                            </UI.Row>
                        </UI.Card>
                    </UI.Col>
                </UI.Row>
                <UI.Row>
                    <UI.Col md={12}>
                        <UI.Card
                            title={t("screens.kredi_flow_settings.fallback_workflowusers.title").toString()}
                            className={styles.VisaworkflowSettingsFormContainer}
                        >
                            <UI.Row className="mb-3">
                                <UI.Col>
                                    <span>
                                        {t("screens.kredi_flow_settings.fallback_workflowusers.explanation_text")}
                                    </span>
                                </UI.Col>
                            </UI.Row>
                            {FALLBACK_FIELDS.map((field) => (
                                <UI.Row className="align-items-center mb-0" key={field}>
                                    <UI.Col md={2} lg={1}>
                                        <label className="mb-3 bold-text" style={{ color: "var(--primary)" }}>
                                            {t(
                                                `screens.communication_settings.krediflow.assignment_and_visa.alternative.${field}`
                                            )}
                                        </label>
                                    </UI.Col>
                                    <UI.Col md={4} lg={3}>
                                        <FallbackWorkflowUserField field={field} />
                                    </UI.Col>
                                </UI.Row>
                            ))}
                        </UI.Card>
                    </UI.Col>
                </UI.Row>
                <div className="mt-4">
                    <UI.Button
                        label={krediFlowConfigurationStore.isLoading ? t("labels.is_saving") : t("labels.save")}
                        disabled={!krediFlowConfigurationStore.hasChanged}
                        onClick={krediFlowConfigurationStore.saveSettings}
                        className={styles.FormButton}
                    />
                    <UI.Button
                        label={t("labels.reset")}
                        disabled={!krediFlowConfigurationStore.hasChanged}
                        onClick={krediFlowConfigurationStore.resetSettings}
                        className={`${styles.FormButton} ml-sm-2 mt-2 mt-sm-0`}
                    />
                </div>
            </UI.Container>
        </React.Fragment>
    );
};

export const KrediflowConfigurationSettings = withRouter(
    Sentry.withProfiler(observer(KrediflowConfigurationSettingsBase))
);
